import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getHistorialCobranza, estimarCorteCobranza, ejecutarCorteCobranza,
} from "../../controllers/api_billing";
import { encryptText } from "../../controllers/api";
import ModalEstimateBilling from "./ModalEstimateBilling";

const fmtMonto = (val) => {
  try {
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return n.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return String(val); }
};

const fmtPeriodo = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const [y, m] = dateStr.split("T")[0].split("-");
    const meses = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio",
                   "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    return `${meses[Number(m)]} ${y}`;
  } catch { return String(dateStr); }
};

export default function ListBillingHistory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estimacion, setEstimacion] = useState(null);
  const [showEstimate, setShowEstimate] = useState(false);
  const [estimating, setEstimating] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getHistorialCobranza({ limit: 100 });
      setRows(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      toast.error("Error al cargar historial.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const onEstimate = async () => {
    setEstimating(true);
    try {
      const r = await estimarCorteCobranza();  // sin periodo => mes anterior
      if (r.estado === "error") {
        toast.error(r.mensaje || "Error al estimar.");
        return;
      }
      setEstimacion(r);
      setShowEstimate(true);
    } finally {
      setEstimating(false);
    }
  };

  const onCortar = async () => {
    if (!estimacion) {
      toast.error("Primero estime el corte para revisar la informacion.");
      return;
    }
    if (estimacion.ya_existe_corte) {
      toast.error("Ya existe un corte ejecutado para ese periodo.");
      return;
    }
    if (!window.confirm(
      `¿Confirmar corte de cobranza para ${fmtPeriodo(estimacion.periodo)}?\n\n` +
      `Empresas a cobrar: ${estimacion.nro_empresas_recaudadas}\n` +
      `Monto total estimado: ${fmtMonto(estimacion.monto_acumulado)} Bs.\n\n` +
      `Esta accion ENVIARA correos a las empresas y NO se puede deshacer.`
    )) return;
    const r = await ejecutarCorteCobranza({ periodo: estimacion.periodo });
    if (r.estado === "ok") {
      toast.success(
        `Corte ejecutado: ${r.nro_empresas_recaudadas} empresa(s), ` +
        `correos enviados=${r.correos_enviados ?? 0}, fallidos=${r.correos_fallidos ?? 0}.`
      );
      setShowEstimate(false);
      setEstimacion(null);
      cargar();
    } else {
      toast.error(r.mensaje || "Error al ejecutar el corte.");
    }
  };

  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-lg">
        <div className="px-6 py-6 flex justify-between items-center border-b flex-wrap gap-3">
          <h6 className="text-blueGray-700 text-xl font-bold">Histórico de cobranza</h6>
          <div className="flex items-center gap-2">
            <button
              onClick={onEstimate}
              disabled={estimating}
              className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold py-2 px-4 rounded shadow disabled:opacity-50"
            >
              <i className="fas fa-calculator mr-1"></i>
              {estimating ? "Estimando..." : "Estimar cobro"}
            </button>
            <button
              onClick={async () => {
                if (!estimacion) {
                  await onEstimate();
                }
                setShowEstimate(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 px-4 rounded shadow"
            >
              <i className="fas fa-paper-plane mr-1"></i> Crear corte
            </button>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-blueGray-400">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-blueGray-400">
              Aún no hay cortes registrados. Use el botón <strong>Estimar cobro</strong> para previsualizar el periodo en curso.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-blueGray-50">
                <tr className="text-blueGray-600">
                  <th className="text-left py-2 px-3">Periodo</th>
                  <th className="text-right py-2 px-3"># Empresas</th>
                  <th className="text-right py-2 px-3"># Facturas</th>
                  <th className="text-right py-2 px-3">Monto Bs.</th>
                  <th className="text-right py-2 px-3">Monto USD*</th>
                  <th className="text-left py-2 px-3">Ejecutado por</th>
                  <th className="text-left py-2 px-3">Fecha</th>
                  <th className="text-center py-2 px-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.id} className="border-t hover:bg-blueGray-50/50">
                    <td className="py-2 px-3 font-semibold capitalize">{fmtPeriodo(h.periodo)}</td>
                    <td className="py-2 px-3 text-right">{h.nro_empresa_recaudadas}</td>
                    <td className="py-2 px-3 text-right font-semibold text-violet-700">{h.nro_facturas_acumulada ?? 0}</td>
                    <td className="py-2 px-3 text-right">{fmtMonto(h.monto_acumulado)}</td>
                    <td className="py-2 px-3 text-right">{fmtMonto(h.monto_acumulado_aplicada)}</td>
                    <td className="py-2 px-3">{h.usuario?.nombre || h.usuario?.email || "—"}</td>
                    <td className="py-2 px-3 text-xs text-blueGray-500">{(h.fecha_calculo || "").split("T")[0]}</td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => {
                          const hash = encryptText(h.id.toString());
                          navigate(`/billing-history/detail?id=${encodeURIComponent(hash)}`);
                        }}
                        title="Ver detalle"
                        className="text-gray-600 hover:text-gray-800 px-2"
                      ><i className="fas fa-expand"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-xs text-blueGray-400 mt-2">
            * Monto USD: equivalente del total Bs. usando la tasa BCV registrada al momento del corte.
          </p>
        </div>
      </div>

      {showEstimate && estimacion && (
        <ModalEstimateBilling
          estimacion={estimacion}
          isOpen={showEstimate}
          onClose={() => setShowEstimate(false)}
          onCortar={onCortar}
        />
      )}
    </div>
  );
}
